import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  InputNumber,
  Button,
  Table,
  Space,
  Typography,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import type {
  IssueMasterData,
  IssueItem,
  Employee,
  Product,
  PO,
  WorkOrder,
  UnitType,
} from "./index";
import { apiRequest } from "../../../../../service/apiRequest";
const { Text } = Typography;

/* ---------------- TYPES ---------------- */

interface Props {
  visible: boolean;
  onCancel: () => void;
  onCreate: () => void;
}
interface IssueMasterModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: () => Promise<void>;
  initialValues?: IssueMasterData | null;
  isEditMode?: boolean;
}
/* ---------------- COMPONENT ---------------- */

const IssueMasterModal: React.FC<IssueMasterModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const [form] = Form.useForm();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [poList, setPoList] = useState<any[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [selectType, setSelectType] = useState<"PI" | "Work" | undefined>(
    undefined,
  );

  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [items, setItems] = useState<IssueItem[]>([]);

  /* ---------------- API LOADERS ---------------- */

  const loadEmployees = async () => {
    const res = await apiRequest({ method: "get", url: "employees?status=1" });
    setEmployees(res);
  };
  const loadunits = async () => {
    const res = await apiRequest({ method: "get", url: "unit-types?status=1" });

    setUnitTypes(res);
  };

  const loadProducts = async () => {
    const res = await apiRequest({
      method: "get",
      url: "get-all-handicraft-products",
    });
    setProducts(res.data);
  };

  const loadPO = async () => {
    const res = await apiRequest({ method: "get", url: "get-performa-invoices" });
    setPoList(res || []);
  };

  const loadWorkOrders = async () => {
    const res = await apiRequest({ method: "get", url: "get-work-orders" });
    setWorkOrders(res);
  };

  useEffect(() => {
    if (visible) {
      Promise.all([
        loadEmployees(),
        loadunits(),
        loadProducts(),
        loadPO(),
        loadWorkOrders(),
      ]).then(() => {
        if (isEditMode && initialValues) {
          form.setFieldsValue({
            issue_to: (initialValues as any).im_issue_to,
            issue_type: (initialValues as any).im_issue_type,
            pi_id: (initialValues as any).im_master_pi_no,
            work_order_id: (initialValues as any).im_master_order_no,
          });
          setSelectType((initialValues as any).im_issue_type);

          if (Array.isArray((initialValues as any).items)) {
            const mappedItems = (initialValues as any).items.map((item: any) => ({
              key: item.imsi_id ? String(item.imsi_id) : Date.now().toString() + Math.random(),
              product_id: item.product_id,
              unit_id: item.unit_id,
              qty: Number(item.qty),
              price: Number(item.price),
              total: Number(item.total)
            }));
            setItems(mappedItems);
          } else {
            setItems([]);
          }
        } else {
          form.resetFields();
          setSelectType(undefined);
          setItems([]);
        }
      }).catch(() => message.error("Failed to load dropdown data"));
    }
  }, [visible, initialValues, isEditMode]);

  /* ---------------- TABLE LOGIC ---------------- */

  const addRow = () => {
    setItems((prev) => [
      ...prev,
      {
        key: Date.now().toString(),
        qty: 1,
        price: 0,
        total: 0,
      },
    ]);
  };

  const updateRow = (key: string, data: Partial<IssueItem>) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const updated = { ...row, ...data };
        updated.total = (updated.qty || 0) * (updated.price || 0);
        return updated;
      }),
    );
  };

  const removeRow = (key: string) => {
    setItems((prev) => prev.filter((r) => r.key !== key));
  };

  const grandTotal = useMemo(
    () => items.reduce((sum, i) => sum + i.total, 0),
    [items],
  );

  /* ---------------- TABLE COLUMNS ---------------- */

  const columns = [
    {
      title: "Product",
      dataIndex: "product_id",
      render: (_: any, record: IssueItem) => (
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Select product"
          style={{ width: "100%" }}
          value={record.product_id}
          onChange={(value) => {
            const product = products.find((p) => p.product_id === value);
            updateRow(record.key, {
              product_id: value,
              price: Number((product as any)?.avg_price ?? product?.product_price ?? 0),
              unit_id: product?.ut_id || undefined,
            });
          }}
        >
          {products.map((p) => (
            <Select.Option
              key={p.product_id}
              value={p.product_id}
              label={p.product_name}
            >
              {p.product_name}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Qty",
      width: 100,
      render: (_: any, record: IssueItem) => (
        <InputNumber
          min={0}
          step={0.01}
          value={record.qty}
          onChange={(val) => updateRow(record.key, { qty: val ?? 0 })}
        />
      ),
    },
    {
      title: "Unit",
      width: 120,
      render: (_: any, record: IssueItem) => {
        const unit = unitTypes.find((u) => u.ut_id === record.unit_id);
        return <Text style={{ fontWeight: 600 }}>{unit?.ut_name || "-"}</Text>;
      },
    },
    {
      title: "Avg Price",
      width: 120,
      render: (_: any, record: IssueItem) => (
        <InputNumber
          min={0}
          value={record.price}
          readOnly
        />
      ),
    },
    {
      title: "Total",
      width: 120,
      render: (_: any, record: IssueItem) => (
        <Text strong>{record.total.toFixed(4)}</Text>
      ),
    },
    {
      title: "",
      width: 50,
      render: (_: any, record: IssueItem) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeRow(record.key)}
        />
      ),
    },
  ];

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      items,
      grand_total: grandTotal,
      issue_master_id: isEditMode && initialValues ? initialValues.im_id : null,
    };

    await apiRequest({
      method: "post",
      url: isEditMode ? "edit-issue-master" : "add-issue-master",
      data: payload,
    });

    message.success(isEditMode ? "Store Issue master updated successfully" : "Store Issue master created successfully");
    form.resetFields();
    setItems([]);
    onCreate();
    onCancel();
  };

  /* ---------------- RENDER ---------------- */

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      width="90%"
      title="Issue Master - Store"
      footer={null}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* ----------- HEADER ----------- */}
        {(() => {
          const colSpan = selectType === "PI" ? 6 : 12;
          return (
            <Row gutter={16}>
              <Col span={colSpan}>
                <Form.Item
                  name="issue_to"
                  label="Issue To"
                  rules={[{ required: true, message: "Please select an employee" }]}
                >
                  <Select
                    placeholder="Select employee"
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={employees.map((emp) => ({
                      label: emp?.employee_fname + " " + emp?.employee_lname,
                      value: emp?.employee_id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={colSpan}>
                <Form.Item
                  name="issue_type"
                  label="Issue Type"
                  rules={[{ required: true, message: "Please select issue type" }]}
                >
                  <Select
                    placeholder="Select Type"
                    onChange={(value: "PI" | "Work") => {
                      setSelectType(value);
                      if (value === "Work") {
                        form.setFieldsValue({ pi_id: undefined, work_order_id: undefined });
                      }
                    }}
                  >
                    <Select.Option value="PI">PI</Select.Option>
                    <Select.Option value="Work">Work</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              {selectType === "PI" && (
                <>
                  <Col span={colSpan}>
                    <Form.Item name="pi_id" label="PI Number" rules={[{ required: true, message: "Please select PI Number" }]}>
                      <Select
                        showSearch
                        placeholder="Search PI Number"
                        optionFilterProp="label"
                        onChange={() => {
                          form.setFieldsValue({ work_order_id: undefined });
                        }}
                      >
                        {poList.map((po: any) => (
                          <Select.Option key={po.pi_id} value={po.pi_id} label={String(po.pi_order_no)}>
                            {po.pi_order_no}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={colSpan}>
                    <Form.Item noStyle dependencies={["pi_id"]}>
                      {({ getFieldValue }) => {
                        const currentPiId = getFieldValue("pi_id");
                        const filteredWorkOrders = currentPiId
                          ? workOrders.filter((wo) => Number(wo.wo_pi_id) === Number(currentPiId))
                          : workOrders;

                        return (
                          <Form.Item name="work_order_id" label="Work Order No" rules={[{ required: true, message: "Please select Work Order" }]}>
                            <Select showSearch placeholder="Search Work Order No" optionFilterProp="label">
                              {filteredWorkOrders.map((wo) => (
                                <Select.Option
                                  key={wo.wo_id}
                                  value={wo.wo_id}
                                  label={wo.wo_number || `WO-${wo.wo_id}`}
                                >
                                  {wo.wo_number || `WO-${wo.wo_id}`}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
          );
        })()}

        {/* ----------- TABLE ----------- */}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addRow}
          style={{ marginBottom: 12 }}
        >
          Add Product
        </Button>

        <Table
          columns={columns}
          dataSource={items}
          pagination={false}
          rowKey="key"
        />

        {/* ----------- TOTAL ----------- */}
        <Row justify="end" style={{ marginTop: 16, flexDirection: "column", alignItems: "flex-end" }}>
          <Text strong style={{ fontSize: 16 }}>
            Grand Total : {grandTotal.toFixed(4)}
          </Text>
        </Row>

        {/* ----------- ACTIONS ----------- */}
        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Submit Issue Master - Store
            </Button>
          </Space>
        </Row>
      </Form>
    </Modal>
  );
};

export default IssueMasterModal;

